import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { User as UserIcon, Save, Loader2 } from "lucide-react";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@akuna/ui";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { ProfilePhotoUpload } from "@/components/profile/ProfilePhotoUpload";
import { useProfile, useUpdateProfile, useUploadProfilePhoto, useDeleteProfilePhoto } from "@/hooks/useProfile";
import { profileSchema, type ProfileInput } from "@/lib/validations/profile";
import { useStates } from "@/hooks/useLookups";
import { useIsVeterinarian } from "@/hooks/useIsVeterinarian";
import { useVetPrescriptionCounter } from "@/hooks/useVetPrescriptionCounter";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { formatCpf } from "@/lib/utils/cpf-mask";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";

const maskPhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    if (digits.length <= 10) {
        const part1 = digits.slice(0, 2);
        const part2 = digits.slice(2, 6);
        const part3 = digits.slice(6, 10);

        return [
            part1 && `(${part1}`,
            part1 && part1.length === 2 ? ") " : "",
            part2,
            part3 && `-${part3}`,
        ]
            .join("")
            .trim();
    }

    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 7);
    const part3 = digits.slice(7, 11);

    return [
        part1 && `(${part1}`,
        part1 && part1.length === 2 ? ") " : "",
        part2,
        part3 && `-${part3}`,
    ]
        .join("")
        .trim();
};

export default function Profile() {
    const { t } = useTranslation("settings");
    const { t: tPrescriptions } = useTranslation("prescriptions");
    useMobileHeader({ title: t("profileTitle") }, [t]);
    const { data: profile, isLoading: isLoadingProfile } = useProfile();
    const updateProfile = useUpdateProfile();
    const uploadPhoto = useUploadProfilePhoto();
    const deletePhoto = useDeleteProfilePhoto();
    const { data: states } = useStates();
    const { data: isVeterinarian, isLoading: isLoadingVeterinarian } = useIsVeterinarian();
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const { counter, updateCounter } = useVetPrescriptionCounter(profile?.id, Boolean(isVeterinarian));
    const [counterValue, setCounterValue] = useState<string>("");

    useEffect(() => {
        if (counter) {
            setCounterValue(String(counter.current_value));
        }
    }, [counter]);

    const form = useForm<ProfileInput>({
        resolver: zodResolver(profileSchema),
        defaultValues: {
            email: "",
            full_name: "",
            phone: "",
            cpf: "",
            crmv_state: "",
            crmv_number: null,
            mapa_number: "",
            address: "",
        },
    });

    // Carregar dados do perfil no formulário
    useEffect(() => {
        if (profile) {
            form.reset({
                email: profile.email || "",
                full_name: profile.full_name || "",
                phone: maskPhoneInput(profile.phone || ""),
                cpf: formatCpf(profile.cpf || ""),
                crmv_state: profile.crmv_state || "",
                crmv_number: profile.crmv_number || null,
                mapa_number: profile.mapa_number || "",
                address: profile.address || "",
            });
        }
    }, [profile, form]);

    // Handler para upload de foto
    const handlePhotoChange = (file: File | null) => {
        setPhotoFile(file);
    };

    // Handler para remover foto
    const handlePhotoRemove = async () => {
        try {
            await deletePhoto.mutateAsync();
            setPhotoFile(null);
        } catch (error) {
            // Error já tratado no hook
        }
    };

    // Handler para submit
    const onSubmit = async (data: ProfileInput) => {
        try {
            // Upload de foto primeiro (se houver)
            if (photoFile) {
                await uploadPhoto.mutateAsync(photoFile);
                setPhotoFile(null);
            }

            // Depois atualizar o perfil
            await updateProfile.mutateAsync(data);
        } catch (error) {
            // Errors já tratados nos hooks
        }
    };

    if (isLoadingProfile) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="hidden md:block">
                <h1 className="text-3xl font-bold flex items-center gap-2">
                    <UserIcon className="h-8 w-8" />
                    {t("profileTitle")}
                </h1>
                <p className="text-muted-foreground mt-2">
                    {t("profileSubtitle")}
                </p>
            </div>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-full space-y-4 sm:space-y-6">
                    <div className="grid max-w-full gap-4 lg:grid-cols-2 lg:gap-6">
                        {/* Foto de Perfil */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t("profilePhotoTitle")}</CardTitle>
                                <CardDescription>
                                    {t("profilePhotoDescription")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ProfilePhotoUpload
                                    value={profile?.avatar_url || null}
                                    onChange={handlePhotoChange}
                                    onRemove={handlePhotoRemove}
                                    disabled={uploadPhoto.isPending || deletePhoto.isPending}
                                />
                            </CardContent>
                        </Card>

                        {/* Dados Pessoais */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t("profilePersonalTitle")}</CardTitle>
                                <CardDescription>
                                    {t("profilePersonalDescription")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("profileEmailLabel")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="email"
                                                    placeholder={t("profileEmailPlaceholder")}
                                                    disabled
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                            <p className="text-xs text-muted-foreground">
                                                {t("profileEmailHint")}
                                            </p>
                                            {/* <p className="text-xs text-muted-foreground">
                                                Ao alterar o email, você receberá um link de confirmação no novo endereço
                                            </p> */}
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="full_name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("profileFullNameLabel")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    placeholder={t("profileFullNamePlaceholder")}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="phone"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("profilePhoneLabel")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="tel"
                                                    placeholder={t("profilePhonePlaceholder")}
                                                    {...field}
                                                    value={field.value || ""}
                                                    inputMode="numeric"
                                                    maxLength={15}
                                                    onChange={(event) => field.onChange(maskPhoneInput(event.target.value))}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="address"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("profileAddressLabel")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    placeholder={t("profileAddressPlaceholder")}
                                                    maxLength={300}
                                                    {...field}
                                                    value={field.value || ""}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="cpf"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("profileCpfLabel")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    placeholder={t("profileCpfPlaceholder")}
                                                    {...field}
                                                    value={field.value || ""}
                                                    inputMode="numeric"
                                                    maxLength={14}
                                                    onChange={(event) => field.onChange(formatCpf(event.target.value))}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Dados Profissionais - Apenas para veterinários */}
                    {!isLoadingVeterinarian && isVeterinarian && (
                        <Card>
                            <CardHeader>
                                <CardTitle>{t("profileProfessionalTitle")}</CardTitle>
                                <CardDescription>
                                    {t("profileProfessionalDescription")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-3 md:grid-cols-2 md:gap-4">
                                    <FormField
                                        control={form.control}
                                        name="crmv_state"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("profileCrmvStateLabel")}</FormLabel>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    value={field.value || ""}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder={t("profileCrmvStatePlaceholder")} />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent className="max-h-[200px] overflow-y-auto">
                                                        {states?.map((state) => (
                                                            <SelectItem key={state.id} value={state.code}>
                                                                {state.name} ({state.code})
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="crmv_number"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("profileCrmvNumberLabel")}</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        placeholder={t("profileNumberPlaceholder")}
                                                        {...field}
                                                        value={field.value || ""}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            field.onChange(value === "" ? null : parseInt(value, 10));
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <FormField
                                    control={form.control}
                                    name="mapa_number"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("profileMapaLabel")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    placeholder={t("profileNumberPlaceholder")}
                                                    {...field}
                                                    value={field.value || ""}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="space-y-2 rounded-lg border border-border/70 p-3">
                                    <Label>{tPrescriptions("profileModal.counterLabel")}</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            min={0}
                                            value={counterValue}
                                            onChange={(e) => setCounterValue(e.target.value)}
                                            placeholder="0"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            disabled={updateCounter.isPending}
                                            onClick={() => {
                                                const parsed = parseInt(counterValue, 10);
                                                if (!Number.isFinite(parsed) || parsed < 0) return;
                                                updateCounter.mutate(parsed);
                                            }}
                                        >
                                            {updateCounter.isPending
                                                ? tPrescriptions("profileModal.saving")
                                                : tPrescriptions("profileModal.updateCounter")}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {tPrescriptions("profileModal.counterHelp")}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardHeader>
                            <CardTitle>{t("profileLanguageTitle")}</CardTitle>
                            <CardDescription>{t("profileLanguageDescription")}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-2 sm:grid-cols-2">
                                <Button
                                    type="button"
                                    variant={i18n.language?.startsWith("pt") ? "default" : "outline"}
                                    onClick={() => i18n.changeLanguage("pt")}
                                >
                                    {t("profileLanguagePortuguese")}
                                </Button>
                                <Button
                                    type="button"
                                    variant={i18n.language?.startsWith("en") ? "default" : "outline"}
                                    onClick={() => i18n.changeLanguage("en")}
                                >
                                    {t("profileLanguageEnglish")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Botão de Salvar */}
                    <div className="flex justify-end gap-2">
                        <Button
                            type="submit"
                            disabled={updateProfile.isPending || uploadPhoto.isPending}
                            className="w-full sm:w-auto sm:min-w-[120px]"
                        >
                            {updateProfile.isPending || uploadPhoto.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("saving")}
                                </>
                            ) : (
                                <>
                                    <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
                                    {t("save")}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
}
